import React from "react";
import { connect } from "react-redux";
import { Guid } from "guid-typescript";

import { IAppState } from "../../state/Store";
import {
    broadcastMessage,
    clearThread,
    sendMessage,
} from "../../state/system/Messaging";
import AppState from "../../foundation/AppState";
import MessagingReceipients from "./MessagingReceipients";
import MessagingBroadcastClose from "./MessagingBroadcastClose";
import MessagingMessages from "./MessagingMessages";
import MessagingBroadcasts from "./MessagingBroadcasts";
import MessagingEditor from "./MessagingEditor";
import MessagingBroadcastSend from "./MessagingBroadcastSend";
import DistributionTypes from "../../types/enums/DistributionTypes";
import RecipientTypes from "../../types/enums/RecipientTypes";
import MessagingMessage from "../../types/system/MessagingMessage";
import AlertEntry, { AlertTypes } from "../../types/system/AlertEntry";
import MessagingContact from "../../types/core/MessagingContact";
import { AuthJWTPayload } from "../../types/system/AuthJWT";
import { addAlert } from "../../state/system/Alerts";

interface MessagingMainProps {
    jwt: AuthJWTPayload;
    isSystem: boolean;
    broadcasting: boolean;
    allowBroadcast: boolean;
    contacts: Array<MessagingContact>;
    recipients: Array<MessagingContact>;
    addAlert: (alert: AlertEntry) => void;
    broadcast: () => void;
    clearThread: () => void;
    onChange: (html: string) => void;
    sendMessage: (message: MessagingMessage) => Promise<Guid>;
    broadcastMessage: (
        message: MessagingMessage,
        target: number,
    ) => Promise<Guid>;
}

interface MessagingMainState {
    body: string;
    disableSend: boolean;
    sending: boolean;
    holdOpen: boolean;
    broadcastType: number;
}

class MessagingMain extends React.Component<
    MessagingMainProps,
    MessagingMainState
> {
    state = {
        body: "",
        disableSend: false,
        sending: false,
        holdOpen: false,
        broadcastType: 1,
    };

    static getDerivedStateFromProps(
        props: MessagingMainProps,
        state: MessagingMainState,
    ): Partial<MessagingMainState> {
        return {
            disableSend: props.recipients?.length === 0,
        };
    }

    async send(broadcast: boolean = false) {
        if (!this.props.recipients.length && !broadcast) {
            this.props.addAlert({
                type: AlertTypes.Alert,
                text: "Your message must have at least one recipient",
            });
            return;
        }

        // Construct message object
        const message = new MessagingMessage(
            Number(this.props.jwt.sub),
            this.props.jwt.schoolId,
            this.props.jwt.applicationId,
            "",
            this.state.body,
        );
        const stripEnd = "<p><br></p>";
        if (
            message.Content.endsWith(stripEnd) &&
            message.Content.length > stripEnd.length
        )
            message.Content = message.Content.substring(
                0,
                message.Content.length - stripEnd.length,
            );

        // Generate attachments
        this.setState({ body: "", sending: true });

        message.setDistribution(DistributionTypes.TDS);

        if (!broadcast) {
            // copy recipients to avoid the attachment generation messing with things
            for (const recipient of this.props.recipients)
                message.addRecipient(RecipientTypes.User, recipient.UserID);
        }

        !broadcast
            ? this.props.sendMessage(message)
            : this.props.broadcastMessage(message, this.state.broadcastType);

        this.setState({ sending: false });
    }

    render(): React.ReactNode {
        return (
            <div className="message-ui col flex-column">
                <div className="col-pane message-toolbar">
                    <button
                        className="action-button"
                        onClick={() => this.props.clearThread()}>
                        <i>chevron_left</i>
                    </button>
                </div>
                <MessagingReceipients
                    isSystem={this.props.isSystem}
                    broadcasting={this.props.broadcasting}
                />
                <MessagingBroadcastClose
                    isSystem={this.props.isSystem}
                    broadcasting={this.props.broadcasting}
                    clearThread={this.props.clearThread}
                />
                <MessagingMessages
                    isSystem={this.props.isSystem}
                    broadcasting={this.props.broadcasting}
                    body={this.state.body}
                />
                <MessagingBroadcasts
                    broadcasting={this.props.broadcasting}
                    broadcastType={this.state.broadcastType}
                    broadcastTypeChanged={type =>
                        this.setState({ broadcastType: type })
                    }
                />
                <MessagingEditor
                    body={this.state.body}
                    isSystem={this.props.isSystem}
                    broadcasting={this.props.broadcasting}
                    disableSend={
                        this.state.disableSend && !this.props.broadcasting
                    }
                    sending={this.state.sending}
                    onChange={html => this.setState({ body: html })}
                    onSend={this.send.bind(this)}
                />
                <MessagingBroadcastSend
                    broadcasting={this.props.broadcasting}
                    disableSend={this.state.disableSend}
                    sending={this.state.sending}
                    send={this.send.bind(this)}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { messaging } = state.db;
    return {
        jwt: AppState.jwt,
        contacts: messaging.contacts,
        recipients: messaging.recipients,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        addAlert: alert => dispatch(addAlert(alert)),
        clearThread: () => dispatch(clearThread()),
        sendMessage: (message: MessagingMessage) =>
            dispatch(sendMessage(message.toMessage())),
        broadcastMessage: (message: MessagingMessage, target: number) =>
            dispatch(broadcastMessage([message.toMessage(), target])),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessagingMain);
